import React, { useEffect, useMemo, useState } from 'react';

import {
  EnvironmentOutlined,
  LinkOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import {
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  message,
} from 'antd';
import PhoneInput from 'antd-phone-input';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { AdminCityRow } from 'models/Lead';
import {
  CreateTrainingInput,
  TrainingDetail,
  TrainingKind,
  UpdateTrainingInput,
} from 'models/Training';

import { LeadService } from 'services/LeadService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import appI18n from '../../../config/i18n';
import {
  isoToNumericPhone,
  numericToDialCode,
} from '../../Leads/Components/leadHelpers';

const COUNTRY_OPTIONS = [{ value: 'CL' }, { value: 'PE' }, { value: 'CO' }];
const DEFAULT_COUNTRY = 'CL';
const DEFAULT_CAPACITY = 20;
const MIN_CAPACITY = 5;

const getRegionLabelKey = (countryCode: string): string => {
  switch ((countryCode || '').toUpperCase()) {
    case 'CL':
      return 'region_cl';
    case 'PE':
      return 'region_pe';
    case 'CO':
      return 'region_co';
    default:
      return 'region_default';
  }
};

const getCityLabelKey = (countryCode: string): string => {
  switch ((countryCode || '').toUpperCase()) {
    case 'CL':
      return 'city_cl';
    case 'PE':
      return 'city_pe';
    case 'CO':
      return 'city_co';
    default:
      return 'city_default';
  }
};

interface Props {
  isOpen: boolean;
  mode?: 'create' | 'edit';
  training?: TrainingDetail | null;
  defaultCountry?: string;
  handleModalCancel: () => void;
  onSaved: () => void;
}

type PhoneValue =
  | string
  | {
      countryCode?: number;
      areaCode?: string;
      phoneNumber?: string;
    }
  | undefined;

interface FormValues {
  countryCode: string;
  name: string;
  startsAt: Dayjs;
  endsAt: Dayjs;
  kind: TrainingKind;
  capacity: number;
  meetingUrl?: string;
  venueName?: string;
  regionId?: string;
  cityId?: number;
  address?: string;
  contactName?: string;
  contactRole?: string;
  contactPhone?: PhoneValue;
  description?: string;
}

function CreateTrainingModal({
  isOpen,
  mode = 'create',
  training,
  defaultCountry,
  handleModalCancel,
  onSaved,
}: Props) {
  const isEditMode = mode === 'edit' && !!training;
  const isLockedStructural = isEditMode && (training?.enrolledCount ?? 0) > 0;
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm<FormValues>();

  const datePart = FunctionsHelper.getDateFormatByLang(i18n.language);
  const dateTimeFormat = `${datePart} h:mm A`;

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [cities, setCities] = useState<AdminCityRow[]>([]);
  const [citiesLoading, setCitiesLoading] = useState<boolean>(false);
  const [citiesCountry, setCitiesCountry] = useState<string>('');

  const kind = Form.useWatch('kind', form);
  const countryCode = Form.useWatch('countryCode', form);
  const selectedRegionId = Form.useWatch('regionId', form);

  // Counter para forzar remount del PhoneInput sólo cuando cambia el país y
  // aún no había número tipeado. No reacciona a los keystrokes del usuario.
  const [phoneInputSeed, setPhoneInputSeed] = useState<number>(0);

  const regionLabel = t(
    `scenes.trainings.scene.create.labels.${getRegionLabelKey(countryCode)}`
  );
  const cityLabel = t(
    `scenes.trainings.scene.create.labels.${getCityLabelKey(countryCode)}`
  );

  useEffect(() => {
    if (!isOpen) return;
    form.resetFields();
    setCities([]);
    setCitiesCountry('');

    if (isEditMode && training) {
      const phoneInit: PhoneValue =
        training.hostPhone && training.hostPhoneCountryCode
          ? `${training.hostPhoneCountryCode}${training.hostPhone}`
          : { countryCode: isoToNumericPhone(training.countryCode) };

      form.setFieldsValue({
        countryCode: training.countryCode,
        name: training.name,
        startsAt: dayjs(training.startsAt),
        endsAt: dayjs(training.endsAt),
        kind: training.kind,
        capacity: training.capacity,
        meetingUrl: training.meetingUrl || undefined,
        venueName: training.venueName || undefined,
        cityId: training.cityId ?? undefined,
        address: training.address || undefined,
        contactName: training.hostName,
        contactRole: training.hostRole,
        contactPhone: phoneInit,
        description: training.description || undefined,
      });
    } else {
      const initialCountry = defaultCountry || DEFAULT_COUNTRY;
      form.setFieldsValue({
        countryCode: initialCountry,
        kind: 'virtual',
        capacity: DEFAULT_CAPACITY,
        contactPhone: {
          countryCode: isoToNumericPhone(initialCountry),
        },
      });
    }
    setPhoneInputSeed((s) => s + 1);
  }, [isOpen, isEditMode, training, defaultCountry, form]);

  useEffect(() => {
    if (!isOpen) return;
    if (kind !== 'in_person') return;
    if (!countryCode) return;
    if (countryCode === citiesCountry) return;
    fetchCities(countryCode);
  }, [isOpen, kind, countryCode]);

  // En modo edit: una vez cargadas las cities, resolvemos regionId desde cityId.
  useEffect(() => {
    if (!isOpen || !isEditMode) return;
    if (cities.length === 0) return;
    const currentCityId = form.getFieldValue('cityId') as number | undefined;
    if (!currentCityId) return;
    if (form.getFieldValue('regionId')) return;
    const match = cities.find((c) => c.id === currentCityId);
    if (match) {
      form.setFieldValue('regionId', match.regionId);
    }
  }, [isOpen, isEditMode, cities, form]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode) return; // país inmutable en edición
    const rawPhone = form.getFieldValue('contactPhone');
    const currentPhone =
      rawPhone && typeof rawPhone === 'object' ? rawPhone : {};
    const hasNumber = currentPhone.areaCode || currentPhone.phoneNumber;

    // Siempre limpiamos región/ciudad al cambiar el país. El teléfono sólo se
    // sincroniza con el nuevo dial code si el usuario aún no tipeó nada.
    form.setFieldsValue({ regionId: undefined, cityId: undefined });

    if (!hasNumber) {
      form.setFieldValue('contactPhone', {
        ...currentPhone,
        countryCode: isoToNumericPhone(countryCode),
      });
      setPhoneInputSeed((s) => s + 1);
    }
  }, [countryCode, isOpen, isEditMode, form]);

  const fetchCities = async (country: string) => {
    setCitiesLoading(true);
    const response = await LeadService.getCities(country);
    if (response.success) {
      setCities(response.data as AdminCityRow[]);
      setCitiesCountry(country);
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.trainings.scene.errors.fetch_cities'),
        3.5
      );
    }
    setCitiesLoading(false);
  };

  const kindOptions = [
    {
      value: 'virtual' as TrainingKind,
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <VideoCameraOutlined style={{ color: '#722ED1' }} />
          <span>{t('scenes.trainings.scene.kinds.virtual')}</span>
        </span>
      ),
    },
    {
      value: 'in_person' as TrainingKind,
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <EnvironmentOutlined style={{ color: '#1677FF' }} />
          <span>{t('scenes.trainings.scene.kinds.in_person')}</span>
        </span>
      ),
    },
  ];

  const countryOptions = useMemo(
    () =>
      COUNTRY_OPTIONS.map((opt) => {
        const code = opt.value.toLowerCase();
        return {
          value: opt.value,
          label: (
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <img
                src={`https://flagcdn.com/16x12/${code}.png`}
                srcSet={`https://flagcdn.com/32x24/${code}.png 2x`}
                width={16}
                height={12}
                alt=""
                style={{ display: 'block' }}
              />
              <span>
                {t(`scenes.trainings.scene.countries.${opt.value}`, opt.value)}
              </span>
            </span>
          ),
        };
      }),
    [t]
  );

  const regions = useMemo(() => {
    const map = new Map<string, string>();
    cities.forEach((c) => {
      if (!map.has(c.regionId)) map.set(c.regionId, c.regionName);
    });
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [cities]);

  const citiesForRegion = useMemo(() => {
    if (!selectedRegionId) return [];
    return cities
      .filter((c) => c.regionId === selectedRegionId)
      .map((c) => ({ value: c.id, label: c.cityName }));
  }, [cities, selectedRegionId]);

  // Parsea el value del PhoneInput. Devuelve atomic pair o undefined.
  const parsePhone = (
    raw: PhoneValue
  ): { hostPhone: string; hostPhoneCountryCode: string } | undefined => {
    if (!raw || typeof raw === 'string') return undefined;
    const dialCode = raw.countryCode
      ? numericToDialCode(raw.countryCode)
      : undefined;
    const number =
      raw.areaCode || raw.phoneNumber
        ? `${raw.areaCode || ''}${raw.phoneNumber || ''}`
        : undefined;
    if (!dialCode || !number) return undefined;
    return { hostPhone: number, hostPhoneCountryCode: dialCode };
  };

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }

    const isInPerson = values.kind === 'in_person';
    const description = values.description?.trim();
    const phoneParsed = parsePhone(values.contactPhone);

    if (isEditMode && training) {
      const body: UpdateTrainingInput = {
        name: values.name.trim(),
        hostName: (values.contactName || '').trim(),
        hostRole: (values.contactRole || '').trim(),
        description: description ? description : null,
      };

      if (!isLockedStructural) {
        body.kind = values.kind;
        body.capacity = values.capacity;
        body.startsAt = values.startsAt.format('YYYY-MM-DDTHH:mm:ss');
        body.endsAt = values.endsAt.format('YYYY-MM-DDTHH:mm:ss');
      }

      if (isInPerson) {
        body.venueName = values.venueName?.trim() || null;
        body.address = values.address?.trim() || null;
        body.meetingUrl = null;
        if (!isLockedStructural) body.cityId = values.cityId ?? null;
      } else {
        body.meetingUrl = values.meetingUrl?.trim() || null;
        body.venueName = null;
        body.address = null;
        if (!isLockedStructural) body.cityId = null;
      }

      // Phone: el usuario interactuó con el componente → mandamos pair.
      // Si no interactuó (sigue string) → omitimos para preservar el valor.
      if (phoneParsed) {
        body.hostPhone = phoneParsed.hostPhone;
        body.hostPhoneCountryCode = phoneParsed.hostPhoneCountryCode;
      }

      setSubmitting(true);
      const response = await LeadService.updateTraining(training.id, body);
      setSubmitting(false);

      if (response.success) {
        handleModalCancel();
        onSaved();
      } else {
        const error = response.data as ApiErrorData;
        messageApi.error(
          (error.message as string) ||
            t('scenes.trainings.scene.edit.errors.update_failed'),
          3.5
        );
      }
      return;
    }

    // Modo CREATE
    const body: CreateTrainingInput = {
      name: values.name.trim(),
      kind: values.kind,
      status: 'open',
      startsAt: values.startsAt.format('YYYY-MM-DDTHH:mm:ss'),
      endsAt: values.endsAt.format('YYYY-MM-DDTHH:mm:ss'),
      capacity: values.capacity,
      countryCode: values.countryCode,
      hostName: (values.contactName || '').trim(),
      hostRole: (values.contactRole || '').trim(),
    };

    if (description) body.description = description;

    if (isInPerson) {
      body.cityId = values.cityId;
      body.venueName = values.venueName?.trim();
      body.address = values.address?.trim();
    } else {
      body.meetingUrl = values.meetingUrl?.trim();
    }

    if (phoneParsed) {
      body.hostPhone = phoneParsed.hostPhone;
      body.hostPhoneCountryCode = phoneParsed.hostPhoneCountryCode;
    }

    setSubmitting(true);
    const response = await LeadService.createTraining(body);
    setSubmitting(false);

    if (response.success) {
      handleModalCancel();
      onSaved();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.trainings.scene.create.errors.create_failed'),
        3.5
      );
    }
  };

  const disablePastDates = (current: Dayjs): boolean => {
    return current && current.isBefore(dayjs().startOf('day'));
  };

  const sectionCardStyle: React.CSSProperties = {
    marginBottom: 16,
    borderRadius: 8,
    background: '#FAFBFC',
    borderColor: '#EEF0F4',
  };

  const sectionHeaderStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    fontSize: 13,
    color: 'rgba(0, 0, 0, 0.78)',
  };

  const renderSectionTitle = (icon: React.ReactNode, label: string) => (
    <span style={sectionHeaderStyle}>
      {icon}
      <span>{label}</span>
    </span>
  );

  const modalTitle = isEditMode
    ? t('scenes.trainings.scene.edit.title')
    : t('scenes.trainings.scene.create.title');
  const modalOkText = isEditMode
    ? t('scenes.trainings.scene.edit.buttons.save')
    : t('scenes.trainings.scene.create.buttons.create');
  const lockedTooltip = isLockedStructural
    ? t('scenes.trainings.scene.edit.locked_tooltip')
    : '';

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={handleModalCancel}
      onOk={handleSubmit}
      okText={modalOkText}
      cancelText={t('scenes.trainings.scene.create.buttons.cancel')}
      confirmLoading={submitting}
      destroyOnClose
      width={720}
      maskClosable={false}
    >
      {contextHolder}
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />
      <Form form={form} layout="vertical" preserve={false}>
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              name="countryCode"
              label={t('scenes.trainings.scene.create.labels.country')}
              rules={[{ required: true }]}
              tooltip={
                isEditMode
                  ? t('scenes.trainings.scene.edit.country_locked_tooltip')
                  : undefined
              }
            >
              <Select options={countryOptions} disabled={isEditMode} />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="name"
              label={t('scenes.trainings.scene.create.labels.name')}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.trainings.scene.create.errors.name_required'
                  ),
                },
                { max: 255 },
              ]}
            >
              <Input
                maxLength={255}
                placeholder={t(
                  'scenes.trainings.scene.create.placeholders.name'
                )}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="startsAt"
              label={t('scenes.trainings.scene.create.labels.starts_at')}
              tooltip={isLockedStructural ? lockedTooltip : undefined}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.trainings.scene.create.errors.starts_at_required'
                  ),
                },
              ]}
            >
              <DatePicker
                disabled={isLockedStructural}
                showTime={{
                  format: 'h:mm A',
                  use12Hours: true,
                  minuteStep: 5,
                }}
                needConfirm={false}
                format={dateTimeFormat}
                style={{ width: '100%' }}
                disabledDate={disablePastDates}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="endsAt"
              label={t('scenes.trainings.scene.create.labels.ends_at')}
              tooltip={isLockedStructural ? lockedTooltip : undefined}
              dependencies={['startsAt']}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.trainings.scene.create.errors.ends_at_required'
                  ),
                },
                ({ getFieldValue }) => ({
                  validator(_, value: Dayjs | undefined) {
                    const startsAt = getFieldValue('startsAt') as
                      | Dayjs
                      | undefined;
                    if (!value || !startsAt) return Promise.resolve();
                    if (value.isAfter(startsAt)) return Promise.resolve();
                    return Promise.reject(
                      new Error(
                        t(
                          'scenes.trainings.scene.create.errors.ends_before_starts'
                        )
                      )
                    );
                  },
                }),
              ]}
            >
              <DatePicker
                disabled={isLockedStructural}
                showTime={{
                  format: 'h:mm A',
                  use12Hours: true,
                  minuteStep: 5,
                }}
                needConfirm={false}
                format={dateTimeFormat}
                style={{ width: '100%' }}
                disabledDate={disablePastDates}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="kind"
              label={t('scenes.trainings.scene.create.labels.kind')}
              tooltip={isLockedStructural ? lockedTooltip : undefined}
              rules={[{ required: true }]}
            >
              <Select options={kindOptions} disabled={isLockedStructural} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="capacity"
              label={t('scenes.trainings.scene.create.labels.capacity')}
              tooltip={isLockedStructural ? lockedTooltip : undefined}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.trainings.scene.create.errors.capacity_required'
                  ),
                },
                {
                  type: 'integer',
                  min: MIN_CAPACITY,
                  message: t(
                    'scenes.trainings.scene.create.errors.capacity_invalid',
                    { min: MIN_CAPACITY }
                  ),
                },
              ]}
            >
              <InputNumber
                min={MIN_CAPACITY}
                style={{ width: '100%' }}
                disabled={isLockedStructural}
              />
            </Form.Item>
          </Col>

          {kind === 'virtual' && (
            <Col xs={24}>
              <Form.Item
                name="meetingUrl"
                label={
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <LinkOutlined style={{ color: '#1677FF' }} />
                    <span>
                      {t('scenes.trainings.scene.create.labels.meeting_url')}
                    </span>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.trainings.scene.create.errors.meeting_url_required'
                    ),
                  },
                  {
                    type: 'url',
                    message: t(
                      'scenes.trainings.scene.create.errors.meeting_url_invalid'
                    ),
                  },
                ]}
              >
                <Input.TextArea
                  rows={2}
                  placeholder="https://meet.example.com/abc"
                  autoSize={{ minRows: 2, maxRows: 3 }}
                />
              </Form.Item>
            </Col>
          )}

          {kind === 'in_person' && (
            <>
              <Col xs={24}>
                <Card
                  size="small"
                  bordered
                  style={sectionCardStyle}
                  title={renderSectionTitle(
                    <EnvironmentOutlined style={{ color: '#1677FF' }} />,
                    t('scenes.trainings.scene.create.sections.location')
                  )}
                >
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item
                        name="venueName"
                        label={t(
                          'scenes.trainings.scene.create.labels.venue_name'
                        )}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'scenes.trainings.scene.create.errors.venue_name_required'
                            ),
                          },
                          { max: 255 },
                        ]}
                      >
                        <Input
                          maxLength={255}
                          placeholder={t(
                            'scenes.trainings.scene.create.placeholders.venue_name'
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="regionId"
                        label={regionLabel}
                        tooltip={isLockedStructural ? lockedTooltip : undefined}
                      >
                        <Select
                          showSearch
                          allowClear
                          loading={citiesLoading}
                          disabled={citiesLoading || isLockedStructural}
                          options={regions}
                          optionFilterProp="label"
                          placeholder={t(
                            'scenes.trainings.scene.create.placeholders.region'
                          )}
                          onChange={() => {
                            form.setFieldValue('cityId', undefined);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="cityId"
                        label={cityLabel}
                        tooltip={isLockedStructural ? lockedTooltip : undefined}
                        dependencies={['regionId']}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'scenes.trainings.scene.create.errors.city_required'
                            ),
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          allowClear
                          loading={citiesLoading}
                          disabled={
                            citiesLoading ||
                            !selectedRegionId ||
                            isLockedStructural
                          }
                          options={citiesForRegion}
                          optionFilterProp="label"
                          placeholder={t(
                            'scenes.trainings.scene.create.placeholders.city'
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item
                        name="address"
                        label={t(
                          'scenes.trainings.scene.create.labels.address'
                        )}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'scenes.trainings.scene.create.errors.address_required'
                            ),
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </>
          )}

          <Col xs={24}>
            <Card
              size="small"
              bordered
              style={sectionCardStyle}
              title={renderSectionTitle(
                <UserOutlined style={{ color: '#1677FF' }} />,
                t('scenes.trainings.scene.create.sections.contact')
              )}
            >
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item
                    name="contactName"
                    label={t(
                      'scenes.trainings.scene.create.labels.contact_name'
                    )}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'scenes.trainings.scene.create.errors.contact_name_required'
                        ),
                      },
                      { max: 255 },
                    ]}
                  >
                    <Input maxLength={255} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="contactRole"
                    label={t(
                      'scenes.trainings.scene.create.labels.contact_role'
                    )}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'scenes.trainings.scene.create.errors.contact_role_required'
                        ),
                      },
                      { max: 255 },
                    ]}
                  >
                    <Input maxLength={255} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="contactPhone"
                    label={t(
                      'scenes.trainings.scene.create.labels.contact_phone'
                    )}
                    style={{ marginBottom: 0 }}
                  >
                    <PhoneInput
                      key={`phone-${phoneInputSeed}`}
                      enableSearch
                      allowClear
                      placeholder={t(
                        'scenes.trainings.scene.create.placeholders.contact_phone'
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="description"
              label={t('scenes.trainings.scene.create.labels.description')}
            >
              <Input.TextArea
                rows={3}
                placeholder={t(
                  'scenes.trainings.scene.create.placeholders.description'
                )}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default CreateTrainingModal;
